import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useRef, useState } from "react";

import Autoplay from "embla-carousel-autoplay";
import Fade from "embla-carousel-fade";

import { Button } from "@/components";
import { ArrowDown } from "@/components/Icons";
import styles from "@/styles/containers/mural.module.scss";
import ClassNames from "embla-carousel-class-names";
import { useTranslations } from "next-intl";
import { DotButton, useDotButton } from "./Highlights/EmblaCarouselDotButton";
import {
  NextButton,
  PrevButton,
  usePrevNextButtons,
} from "./Highlights/PrevNextButtons";

const Canvas = ({ imageUrl }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const img = new Image();

    img.onload = function () {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      context.drawImage(img, 0, 0, canvas.width, canvas.height);
    };

    img.src = imageUrl;
  }, [imageUrl]);

  return <canvas ref={canvasRef} />;
};

const Highlight = ({ highlight }) => {
  return (
    <div className={styles.emblaSlide} id={highlight.id}>
      <div className={styles.highlight}>
        <div className={styles.picture}>
          <Canvas
            imageUrl={`${process.env.NEXT_PUBLIC_CDN_ASSETS}/${highlight.image}`}
          />
        </div>
        <div className={styles.details}>
          <p className={styles.name}>{highlight.name}</p>
          <p className={styles.description}>{highlight.description}</p>
        </div>
      </div>
    </div>
  );
};

const Highlights = ({ highlights, onSelectProduct, onClose }) => {
  const [selectedProduct, setSelectedProduct] = useState([]);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      slidesToScroll: 1,
    },
    [Autoplay({ playOnInit: true, delay: 10000 }), Fade(), ClassNames()]
  );

  const onButtonClick = useCallback((emblaApi) => {
    const { autoplay } = emblaApi.plugins();
    if (!autoplay) return;
    if (autoplay.options.stopOnInteraction !== false) autoplay.stop();
  }, []);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi, onButtonClick);

  const logSlidesInView = useCallback(() => {
    const element = document.querySelector("#mural .is-snapped.is-in-view");

    if (element) {
      const id = element?.id;
      const product = highlights.find((h) => h.id === id);
      setSelectedProduct(product);
    }
  }, []);

  useEffect(() => {
    if (emblaApi) emblaApi.on("select", logSlidesInView);
    if (emblaApi) emblaApi.on("slidesInView", logSlidesInView);
  }, [emblaApi, logSlidesInView]);

  const { selectedIndex, scrollSnaps } = useDotButton(emblaApi);

  const t = useTranslations();

  return (
    <div className={styles.embla}>
      <div className={styles.close} onClick={() => onClose && onClose()}>
        <ArrowDown />
      </div>

      <div className={styles.emblaViewport} ref={emblaRef}>
        <div className={styles.emblaContainer} id="mural">
          {highlights.map((highlight, _) => {
            return <Highlight highlight={highlight} key={highlight.id} />;
          })}
        </div>
      </div>

      <div className={styles.try}>
        <Button onClick={() => onSelectProduct(selectedProduct)}>
          {t("tryIt")}
        </Button>
      </div>

      <div className={styles.dots}>
        {scrollSnaps.map((_, index) => (
          <DotButton
            key={index}
            className={`${styles.dot}`.concat(
              index === selectedIndex ? ` ${styles.selected}` : ""
            )}
          />
        ))}
      </div>

      {highlights.length > 1 && (
        <div className={styles.emblaButtons} style={{ borderColor: "#5c69e5" }}>
          <PrevButton
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
            color={"#5c69e5"}
          />
          <NextButton
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
            color={"#5c69e5"}
          />
        </div>
      )}
    </div>
  );
};

export default function Mural({ products, onSelectProduct, onClose }) {
  return products.length > 0 ? (
    <Highlights
      highlights={products}
      onSelectProduct={(product) => onSelectProduct(product)}
      onClose={onClose}
    />
  ) : null;
}
